import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../services/api';

interface PendingCountResponse {
  total: number;
  by_type: {
    create: number;
    update: number;
    claim: number;
  };
}

interface UseAdminPendingCountReturn {
  total: number;
  byType: {
    create: number;
    update: number;
    claim: number;
  };
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook สำหรับดึงจำนวน pending requests ที่รออนุมัติ
 * ใช้สำหรับแสดง badge ใน Admin Dashboard และ Sidebar
 *
 * @param autoFetch - ดึงข้อมูลอัตโนมัติเมื่อ mount (default: true)
 * @param refreshInterval - ระยะเวลา auto-refresh (ms), null = ไม่ auto-refresh
 * @returns {UseAdminPendingCountReturn}
 *
 * @example
 * ```tsx
 * // ใช้งานพื้นฐาน
 * const { total, byType, loading, error } = useAdminPendingCount();
 *
 * // Auto-refresh ทุก 30 วินาที
 * const { total } = useAdminPendingCount(true, 30000);
 *
 * // Manual fetch
 * const { total, refetch } = useAdminPendingCount(false);
 * useEffect(() => { refetch(); }, [someDependency]);
 * ```
 */
export const useAdminPendingCount = (
  autoFetch: boolean = true,
  refreshInterval: number | null = null
): UseAdminPendingCountReturn => {
  const [total, setTotal] = useState<number>(0);
  const [byType, setByType] = useState({
    create: 0,
    update: 0,
    claim: 0
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPendingCount = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('ไม่พบ access token กรุณาเข้าสู่ระบบใหม่');
      }

      
      const response = await axios.get<PendingCountResponse>(
        `${API_URL}/api/admin/change-requests/pending-requests/count`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setTotal(response.data.total);
      setByType(response.data.by_type);
    } catch (err: any) {
      console.error('Error fetching pending requests count:', err);

      // Handle specific error cases
      if (err.response?.status === 401) {
        setError('กรุณาเข้าสู่ระบบใหม่');
      } else if (err.response?.status === 403) {
        setError('คุณไม่มีสิทธิ์เข้าถึงข้อมูลนี้');
      } else {
        setError(err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
      }

      // Reset data on error
      setTotal(0);
      setByType({ create: 0, update: 0, claim: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchPendingCount();
    }
  }, [autoFetch, fetchPendingCount]);

  // Auto-refresh interval
  useEffect(() => {
    if (refreshInterval && refreshInterval > 0) {
      const intervalId = setInterval(() => {
        fetchPendingCount();
      }, refreshInterval);

      return () => clearInterval(intervalId);
    }
  }, [refreshInterval, fetchPendingCount]);

  return {
    total,
    byType,
    loading,
    error,
    refetch: fetchPendingCount
  };
};

export default useAdminPendingCount;
